import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { TableModal } from '@shared/components/modals/TableModal';
import { IOption } from '@shared/interfaces';
import { SERVICES } from '@shared/constants/apiConfig';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Wrapper } from '@styles/CommonStyles';
import { ILenderTabCurrentData, ILenderTabData } from '../interfaces';
import { requestCreateLender, requestDeleteLender, requestUpdateLender } from '../requests';
import { lenderTabColumns, lenderTabFormConfig } from './constants';
import { lenderTabSchema } from './validation';

interface ILenderTabProps {
  stipTypeOptions: IOption[];
  lenderList: IOption[];
}

export const LenderTab = ({ stipTypeOptions, lenderList }: ILenderTabProps) => {
  const { data: lendersData, mutate: reloadLenders } = useSWR<ILenderTabData[]>(`/${SERVICES.DOCUMENT}/titleScan/lender`, {revalidateOnMount: undefined});
  const [currentData, setCurrentData] = useState<ILenderTabCurrentData>();
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (data: ILenderTabData) => {
    setSubmitting(true);
    if (data.id) {
      await requestUpdateLender(data);
    } else {
      await requestCreateLender(data);
    }
    reloadLenders();
    setOpenModal(false);
    setSubmitting(false);
  }, [reloadLenders]);

  const handleNewBtn = useCallback(() => {
    setOpenModal(true);
    setCurrentData({} as ILenderTabCurrentData);
  }, []);

  const gridActions = {
    onEdit: (data: ILenderTabData) => {
      setCurrentData({
        ...data,
        lenderId: data?.lenderId,
        stipType: data?.stipType?.map((stipType) => stipType?.id),
      });
      setOpenModal(true);
    },
    onDelete: async (data: ILenderTabData) => {
      await requestDeleteLender(data?.id || '');
      reloadLenders();
    },
  };

  const editingData = useMemo<ILenderTabData | ILenderTabCurrentData>(
    () => ({
      id: currentData?.id || null,
      lender: currentData?.lender || '',
      lenderId: currentData?.lenderId || '',
      stipType: currentData?.stipType || [],
    }),
    [currentData]
  );

  const lenderFromConfig = lenderTabFormConfig(stipTypeOptions, lenderList);

  return (
    <Wrapper>
      <TableModal
        isOpen={!!openModal}
        title="Title Scan Lender"
        editingData={editingData as ILenderTabData}
        formConfig={lenderFromConfig}
        validationSchema={lenderTabSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={() => setOpenModal(false)}
      />
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        isFullHeightColumn
        buttonTitle="Add New Record"
        columns={lenderTabColumns}
        actions={gridActions}
        rows={lendersData || []}
		getRowHeight={() => 'auto'}
        loading={!lendersData}
        onButtonClick={handleNewBtn}
      />
    </Wrapper>
  );
};
